import { useCallback, useEffect, useRef, useState } from "react";

export type StripeIntentAuthenticationPhase =
  | "idle"
  | "confirming"
  | "authentication_failed"
  | "reconciling"
  | "reconciliation_failed"
  | "complete";

interface AuthenticationState {
  clientSecret: string;
  phase: StripeIntentAuthenticationPhase;
  stripeConfirmed: boolean;
}

export interface UseStripeIntentAuthenticationOptions {
  clientSecret: string;
  confirmIntent: () => Promise<boolean>;
  onAuthenticated: (authenticatedClientSecret: string) => Promise<void>;
  onProcessingChange?: (processing: boolean) => void;
}

export interface StripeIntentAuthenticationState {
  phase: StripeIntentAuthenticationPhase;
  busy: boolean;
  authenticate: () => Promise<void>;
}

/** Keep Stripe confirmation distinct from the application's reconciliation. */
export function useStripeIntentAuthentication({
  clientSecret,
  confirmIntent,
  onAuthenticated,
  onProcessingChange,
}: UseStripeIntentAuthenticationOptions): StripeIntentAuthenticationState {
  const [storedState, setStoredState] = useState<AuthenticationState>(() => ({
    clientSecret,
    phase: "idle",
    stripeConfirmed: false,
  }));
  const activeClientSecretRef = useRef(clientSecret);
  activeClientSecretRef.current = clientSecret;
  const operationRef = useRef<{
    clientSecret: string;
    lifecycle: number;
  } | null>(null);
  const lifecycleRef = useRef(0);
  const processingChangeRef = useRef(onProcessingChange);
  processingChangeRef.current = onProcessingChange;
  const [operationInFlight, setOperationInFlight] = useState(false);
  const state: AuthenticationState =
    storedState.clientSecret === clientSecret
      ? storedState
      : { clientSecret, phase: "idle", stripeConfirmed: false };
  const busy =
    operationInFlight ||
    state.phase === "confirming" ||
    state.phase === "reconciling";

  useEffect(
    () => () => {
      lifecycleRef.current += 1;
      if (operationRef.current) {
        operationRef.current = null;
        processingChangeRef.current?.(false);
      }
    },
    [],
  );

  const authenticate = useCallback(async () => {
    if (operationRef.current || state.phase === "complete") {
      return;
    }

    const operation = {
      clientSecret,
      lifecycle: lifecycleRef.current,
    };
    operationRef.current = operation;
    setOperationInFlight(true);
    onProcessingChange?.(true);
    let stripeConfirmed = state.stripeConfirmed;
    const storeState = (
      phase: StripeIntentAuthenticationPhase,
      confirmed: boolean,
    ): void => {
      if (
        lifecycleRef.current !== operation.lifecycle ||
        operationRef.current !== operation ||
        activeClientSecretRef.current !== clientSecret
      ) {
        return;
      }
      setStoredState({ clientSecret, phase, stripeConfirmed: confirmed });
    };

    try {
      if (!stripeConfirmed) {
        storeState("confirming", false);
        if (!(await confirmIntent())) {
          storeState("authentication_failed", false);
          return;
        }
        // The parent may replace the active intent while Stripe's modal is open.
        // Never let that stale completion reconcile or clear the replacement.
        if (
          lifecycleRef.current !== operation.lifecycle ||
          operationRef.current !== operation ||
          activeClientSecretRef.current !== clientSecret
        ) {
          return;
        }
        stripeConfirmed = true;
      }

      if (
        lifecycleRef.current !== operation.lifecycle ||
        operationRef.current !== operation ||
        activeClientSecretRef.current !== clientSecret
      ) {
        return;
      }
      storeState("reconciling", true);
      await onAuthenticated(clientSecret);
      storeState("complete", true);
    } catch {
      storeState(
        stripeConfirmed ? "reconciliation_failed" : "authentication_failed",
        stripeConfirmed,
      );
    } finally {
      if (
        lifecycleRef.current === operation.lifecycle &&
        operationRef.current === operation
      ) {
        operationRef.current = null;
        setOperationInFlight(false);
        onProcessingChange?.(false);
      }
    }
  }, [
    clientSecret,
    confirmIntent,
    onAuthenticated,
    onProcessingChange,
    state.phase,
    state.stripeConfirmed,
  ]);

  return { phase: state.phase, busy, authenticate };
}
